import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, Link2, Plus, Unlink } from "lucide-react";
import { cn } from "@/lib/utils";
import { importanceToneClass } from "@/lib/importance";
import { markPersonalPriorityActivity } from "@/lib/personalPriorityActivity";
import { PersonalPriorityEditorDialog } from "@/components/home/PersonalPriorityEditorDialog";
import { UnlinkPersonalPriorityDialog } from "./UnlinkPersonalPriorityDialog";

/**
 * Stage 3C — "My work for this priority": the OWNER-ONLY private panel on an
 * ACTIVE team-direction row. Lets the current user privately connect their own
 * personal priorities (Stage 1) to this team priority (Stage 2A) via the
 * owner-only Stage 3B procedures.
 *
 * PRIVACY UX (Stage 3A plan §8, all contract-tested):
 *   - The COLLAPSED face is STATE-INVARIANT: identical at 0 and N links — no
 *     count, no badge, no "connected" indicator, nothing derived from link
 *     state. Nothing shows on a shared screen unless the owner deliberately
 *     expands the panel.
 *   - LAZY PRIVACY: no query runs until the owner expands (mineForTeamPriority)
 *     or opens the picker (personalPriorities.forWeek). No private titles in
 *     DOM or network pre-tap. Expansion state is never persisted.
 *   - No affordance is ever disabled based on private-data emptiness — the
 *     zero-priorities case is an invitation INSIDE the picker.
 *   - Every mutation payload is ids-only; the OWNER derives from the session
 *     server-side (ctx.user.id) — no userId ever leaves this component.
 */
export interface MyWorkPanelProps {
  teamPriorityId: number;
  /** The team-direction period — personal priorities use the same week key. */
  weekStartKey: string;
  isAr: boolean;
}

const statusLabel = (s: string, ar: boolean) =>
  ({
    pending: ar ? "لم يبدأ" : "Not started",
    in_progress: ar ? "قيد التنفيذ" : "In progress",
    done: ar ? "منجز" : "Done",
    dropped: ar ? "متروك" : "Dropped",
  })[s] ?? s;

const impLabel = (n: number, ar: boolean) =>
  (ar ? ["", "عالٍ", "متوسط", "منخفض"] : ["", "High", "Medium", "Low"])[n] ?? "";

export function MyWorkPanel({ teamPriorityId, weekStartKey, isAr }: MyWorkPanelProps) {
  const t = (en: string, ar: string) => (isAr ? ar : en);
  const [expanded, setExpanded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [creatorOpen, setCreatorOpen] = useState(false);
  // Unlink is now CONFIRMED (My-work polish): hold ONLY the target personal
  // priority id — never any other-user or count data — so the confirm stays
  // owner-only and link-agnostic. teamPriorityId is fixed for this panel.
  const [unlinkTargetId, setUnlinkTargetId] = useState<number | null>(null);
  const utils = trpc.useUtils();

  // LAZY: nothing is fetched until the owner deliberately expands.
  const mine = trpc.personalPriorityLinks.mineForTeamPriority.useQuery(
    { teamPriorityId },
    { enabled: expanded },
  );
  // The picker lists the owner's OWN current-week personal priorities — only
  // fetched once the picker itself is opened.
  const myWeek = trpc.personalPriorities.forWeek.useQuery(
    { weekStartKey },
    { enabled: expanded && pickerOpen },
  );

  const invalidate = () => {
    // The panel's own rows for THIS team priority…
    utils.personalPriorityLinks.mineForTeamPriority.invalidate({ teamPriorityId });
    // …AND the other owner-only surfaces rendering the same data: Home's
    // My-priorities card (personalPriorities.forWeek) and its connected-to
    // tags (personalPriorityLinks.mineForWeek). The app runs staleTime 30s
    // with focus-refetch off (native-webview tuning), so WITHOUT this
    // cross-invalidation a link/create/unlink here leaves Home reading a
    // fresh-looking stale cache until a manual browser refresh.
    utils.personalPriorityLinks.mineForWeek.invalidate();
    utils.personalPriorities.forWeek.invalidate();
  };
  const link = trpc.personalPriorityLinks.link.useMutation({
    onSuccess: (_data, variables) => {
      // Recent activity on the OWN personal priority — floats it up on Home's
      // recent-first order (Home reads the marker on its next mount).
      markPersonalPriorityActivity(variables.personalPriorityId);
      invalidate();
      setPickerOpen(false);
    },
  });
  const unlink = trpc.personalPriorityLinks.unlink.useMutation({
    onSuccess: (_data, variables) => {
      markPersonalPriorityActivity(variables.personalPriorityId);
      invalidate();
      // Unlink removes only the CONNECTION — point the owner to where the
      // personal priority still lives (the mobile-usability loop closer).
      toast.success(
        t(
          "Unlinked from this team direction. You can still manage it in My priorities.",
          "تم فك الربط من هذا التوجيه. ما زال بإمكانك إدارتها في أولوياتي.",
        ),
      );
    },
  });
  const createFor = trpc.personalPriorityLinks.createForTeamPriority.useMutation({
    onSuccess: () => {
      invalidate();
      setCreatorOpen(false);
    },
  });

  const items = mine.data ?? [];
  const linkedIds = new Set(items.map(i => i.personalPriorityId));
  const pickable = (myWeek.data ?? []).filter(p => !linkedIds.has(p.id));

  return (
    <div className="mt-2.5 border-t border-border/40 pt-2" data-testid={`mywork-${teamPriorityId}`}>
      {/* COLLAPSED FACE — state-invariant: renders these exact nodes whether
          the owner has 0 or 100 linked items (nothing here reads link data). */}
      {/* PRIMARY affordance of the row (visual hierarchy pass): the app's
          primary text-action treatment (coral, font-medium, larger than the
          footnote-weight Stage 2A relevance bookmark below it). Still
          state-invariant: nothing here reads link data. */}
      <button
        type="button"
        data-testid={`mywork-toggle-${teamPriorityId}`}
        onClick={() => setExpanded(e => !e)}
        aria-expanded={expanded}
        // NOT a hyperlink: no underline. Tappable via chevron + coral + medium
        // weight; a subtle hover tint replaces the link-style underline.
        className="inline-flex items-center gap-1.5 text-sm font-medium text-coral hover:text-coral/80 transition-colors"
      >
        <ChevronDown
          className={cn("h-4 w-4 transition-transform", expanded && "rotate-180")}
        />
        {t("My work for this priority", "عملي لهذه الأولوية")}
        {/* NO privacy suffix here — the expanded panel's "Private to you —…"
            helper is the ONE private explanation (duplicate-copy fix). */}
      </button>

      {expanded ? (
        <div className="mt-2 space-y-2" data-testid={`mywork-panel-${teamPriorityId}`}>
          <p className="text-[11px] text-muted-foreground" data-testid={`mywork-scope-${teamPriorityId}`}>
            {t(
              "Private to you. These are your personal priorities connected to this team priority.",
              "خاصة بك. هذه أولوياتك الشخصية المرتبطة بأولوية الفريق هذه.",
            )}
          </p>
          {items.length === 0 && !mine.isLoading ? (
            <p
              className="text-xs text-muted-foreground"
              data-testid={`mywork-empty-${teamPriorityId}`}
            >
              {t(
                "No personal work linked yet. Link or create a private personal priority for this team direction.",
                "لا يوجد عمل شخصي مرتبط بعد. اربط أو أنشئ أولوية شخصية خاصة لهذا التوجيه.",
              )}
            </p>
          ) : (
            <ul className="space-y-1.5">
              {items.map(l => (
                /* STACKED row layout (mobile-readability fix):
                   line 1 = title · line 2 = private label + Importance +
                   Status · line 3 = bounded description preview · action row
                   = Unlink, end-aligned so it never competes with the badges.
                   No counts, no member names, no manager-visible data. */
                <li
                  key={l.id}
                  data-testid={`mywork-item-${l.id}`}
                  className="rounded-xl bg-muted/30 px-3 py-2.5 space-y-1.5"
                >
                  {/* Title wraps cleanly — never truncated before it wraps. */}
                  <span
                    className="block text-sm font-medium break-words"
                    data-testid={`mywork-title-${l.id}`}
                  >
                    {l.personalPriority.title}
                  </span>
                  {/* Badges + status wrap cleanly to a new line instead of
                      crowding one row (items-start + gap-y). */}
                  <div className="flex items-start gap-x-2 gap-y-1 flex-wrap">
                    {/* private/self label — personal rows must never look
                        like shared team rows (workspace clarity) */}
                    <Badge
                      variant="outline"
                      className="text-[10px]"
                      data-testid={`mywork-private-${l.id}`}
                    >
                      {t("Private personal priority", "أولوية شخصية خاصة")}
                    </Badge>
                    <Badge
                      variant="secondary"
                      className={cn(
                        "text-[10px]",
                        importanceToneClass(l.personalPriority.priority),
                      )}
                      title={isAr ? "الأهمية" : "Importance"}
                    >
                      {impLabel(l.personalPriority.priority, isAr)}
                    </Badge>
                    <span className="text-[11px] text-muted-foreground self-center">
                      {statusLabel(l.personalPriority.status, isAr)}
                    </span>
                  </div>
                  {/* Description: up to 3 readable lines inside the expanded
                      panel; only VERY long text is clamped. */}
                  {l.personalPriority.description ? (
                    <p
                      className="text-xs text-muted-foreground line-clamp-3"
                      data-testid={`mywork-desc-${l.id}`}
                    >
                      {l.personalPriority.description}
                    </p>
                  ) : null}
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 text-xs"
                      disabled={unlink.isPending}
                      data-testid={`mywork-unlink-${l.id}`}
                      onClick={() => setUnlinkTargetId(l.personalPriorityId)}
                    >
                      <Unlink className="h-3 w-3 me-1" /> {t("Unlink", "فك الربط")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="rounded-full text-xs"
              data-testid={`mywork-link-existing-${teamPriorityId}`}
              onClick={() => setPickerOpen(o => !o)}
            >
              <Link2 className="h-3 w-3 me-1" />
              {t("Link existing personal priority", "اربط أولوية شخصية موجودة")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full text-xs"
              data-testid={`mywork-create-${teamPriorityId}`}
              onClick={() => setCreatorOpen(true)}
            >
              <Plus className="h-3 w-3 me-1" />
              {t("Create personal priority for this", "أنشئ أولوية شخصية لهذا")}
            </Button>
          </div>

          {pickerOpen ? (
            <div
              className="rounded-xl border border-border/50 p-2 space-y-2"
              data-testid={`mywork-picker-${teamPriorityId}`}
            >
              {pickable.length === 0 && !myWeek.isLoading ? (
                <p className="text-xs text-muted-foreground px-1 py-0.5">
                  {t(
                    "No personal priorities this week. Create one for this instead.",
                    "لا أولويات شخصية هذا الأسبوع. أنشئ واحدة لهذا بدلاً من ذلك.",
                  )}
                </p>
              ) : (
                pickable.map(p => (
                  /* Tappable CARD row (mobile-polish): bordered, padded, full
                     tap target with a chevron so it reads as selectable, not
                     plain text. Own personal priorities only. */
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`mywork-pick-${p.id}`}
                    disabled={link.isPending}
                    onClick={() =>
                      link.mutate({ personalPriorityId: p.id, teamPriorityId })
                    }
                    className="flex w-full items-center justify-between gap-2 rounded-lg border border-border/60 bg-card px-3 py-2.5 text-start hover:bg-muted/50 active:scale-[0.99] transition disabled:opacity-50"
                  >
                    <span className="min-w-0 flex-1 text-sm font-medium break-words">{p.title}</span>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </button>
                ))
              )}
            </div>
          ) : null}

          <PersonalPriorityEditorDialog
            open={creatorOpen}
            onOpenChange={o => {
              if (!o) setCreatorOpen(false);
            }}
            isAr={isAr}
            heading={t("Create personal priority for this", "أنشئ أولوية شخصية لهذا")}
            contextNote={t(
              "This will connect your personal priority to this team direction.",
              "سيؤدي هذا إلى ربط أولويتك الشخصية بتوجيه الفريق هذا.",
            )}
            busy={createFor.isPending}
            onSubmit={vals =>
              createFor.mutate({ teamPriorityId, weekStartKey, ...vals })
            }
          />

          {/* Unlink confirmation — NEUTRAL, reversible-feeling (the personal
              priority + its sibling links stay). Confirming uses the SAME
              unlink mutation (its cache invalidation + link-activity ordering
              are unchanged), so multi-link/sibling behavior is untouched. */}
          <UnlinkPersonalPriorityDialog
            open={unlinkTargetId != null}
            onOpenChange={o => {
              if (!o) setUnlinkTargetId(null);
            }}
            isAr={isAr}
            busy={unlink.isPending}
            onConfirm={() => {
              if (unlinkTargetId != null) {
                unlink.mutate(
                  { personalPriorityId: unlinkTargetId, teamPriorityId },
                  { onSuccess: () => setUnlinkTargetId(null) },
                );
              }
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
